/**
 * SoleadLab 2.0 - Advanced Rules Panel
 *
 * Lists site-level cross-entity alarm rules with add/edit/delete controls.
 * Displayed inside the Alarms ticket in MonitoringPanel.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useMonitoringStore, useUIStore } from '@/stores';
import type { AdvancedAlarmRule, AdvancedRuleType } from '@/types/monitoring';
import './AdvancedRulesPanel.css';

const EMPTY_RULES: AdvancedAlarmRule[] = [];

const TYPE_LABELS: Record<AdvancedRuleType, string> = {
  zero_check: 'Zero Check',
  deviation: 'Deviation',
  expression: 'Expression',
};

function describeAdvancedRule(rule: AdvancedAlarmRule): string {
  if (rule.type === 'zero_check' && rule.zero_check) {
    const c = rule.zero_check;
    return `${c.metric_pattern}* ${c.op} ${c.value} (${c.match_mode})`;
  }
  if (rule.type === 'deviation' && rule.deviation) {
    const c = rule.deviation;
    return `${c.metric} across ${c.entity_type}, >${c.threshold_pct}% from ${c.reference}`;
  }
  if (rule.type === 'expression' && rule.expression) {
    const c = rule.expression;
    const conds = c.conditions
      .map((cd) => `${cd.left} ${cd.op} ${cd.right}`)
      .join(` ${c.combinator} `);
    return conds;
  }
  return '';
}

export default function AdvancedRulesPanel() {
  const advancedRules = useMonitoringStore((s) => s.projectConfig?.advanced_rules ?? EMPTY_RULES);
  const updateAdvancedRule = useMonitoringStore((s) => s.updateAdvancedRule);
  const removeAdvancedRule = useMonitoringStore((s) => s.removeAdvancedRule);
  const openModal = useUIStore((s) => s.openModal);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const addNotification = useUIStore((s) => s.addNotification);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.right - 200 });
    }
    setShowDropdown((prev) => !prev);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const handleAdd = (type: AdvancedRuleType) => {
    setShowDropdown(false);
    openModal('advancedRule', { mode: 'create', ruleType: type });
  };

  const handleEdit = (rule: AdvancedAlarmRule) => {
    openModal('advancedRule', { mode: 'edit', ruleId: rule.id });
  };

  const handleToggle = async (rule: AdvancedAlarmRule) => {
    // B3a — canonical enable/disable write; notify on failure (conflict/error from the store).
    const ok = await updateAdvancedRule(rule.id, { enabled: !rule.enabled });
    if (!ok) addNotification({ type: 'error', title: 'Could not update rule', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
  };

  const handleDelete = (rule: AdvancedAlarmRule) => {
    showConfirm({
      title: 'Delete advanced rule',
      message: `Delete the advanced rule "${rule.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      // B3a — canonical DELETE; notify on failure.
      onConfirm: async () => {
        const ok = await removeAdvancedRule(rule.id);
        if (!ok) addNotification({ type: 'error', title: 'Could not delete rule', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
      },
    });
  };

  return (
    <div className="adv-rules">
      <div className="adv-rules__header">
        <span className="mp-section-label">Advanced Rules ({advancedRules.length})</span>
        <div className="adv-rules__add-wrap">
          <button
            ref={btnRef}
            className="adv-rules__add-btn"
            onClick={toggleDropdown}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Rule
          </button>
          {showDropdown && dropdownPos && (
            <div
              ref={dropdownRef}
              className="adv-rules__dropdown"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
            >
              <button className="adv-rules__dropdown-item" onClick={() => handleAdd('zero_check')}>
                Zero / Threshold Check
                <small>Check metrics by prefix pattern</small>
              </button>
              <button className="adv-rules__dropdown-item" onClick={() => handleAdd('deviation')}>
                Deviation Detection
                <small>Compare metric across entities</small>
              </button>
              <button className="adv-rules__dropdown-item" onClick={() => handleAdd('expression')}>
                Custom Expression
                <small>Build variable-based conditions</small>
              </button>
            </div>
          )}
        </div>
      </div>

      {advancedRules.length === 0 ? (
        <p className="adv-rules__empty">No advanced rules configured</p>
      ) : (
        advancedRules.map((rule) => (
          <div key={rule.id} className={`adv-rule-card adv-rule-card--${rule.severity}`}>
            <div className="adv-rule-card__top">
              <span className={`adv-rule-card__type adv-rule-card__type--${rule.type}`}>
                {TYPE_LABELS[rule.type]}
              </span>
              <span className="adv-rule-card__name" title={rule.name}>{rule.name}</span>
              <button
                className={`adv-rule-card__toggle adv-rule-card__toggle--${rule.enabled ? 'on' : 'off'}`}
                onClick={() => handleToggle(rule)}
                title={rule.enabled ? 'Disable' : 'Enable'}
              />
            </div>
            <div className="adv-rule-card__desc">
              {describeAdvancedRule(rule)}
              {(rule.exclusions?.length ?? 0) > 0 && (
                <span className="adv-rule-card__excl-badge" title={rule.exclusions!.map((e) => `${e.entity_id}.${e.metric}`).join(', ')}>
                  {rule.exclusions!.length} excl.
                </span>
              )}
            </div>

            <div className="adv-rule-card__actions">
              <button className="adv-rule-card__action" onClick={() => handleEdit(rule)} title="Edit">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button className="adv-rule-card__action adv-rule-card__action--danger" onClick={() => handleDelete(rule)} title="Delete">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
